import React, { useState } from 'react'
import { Pin, Edit2, Trash2, AlertCircle } from 'lucide-react'
import { Announcement } from '../../services/communicationsService'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface AnnouncementsListProps {
  announcements: Announcement[]
  onEdit: (announcement: Announcement) => void
  onDelete: (id: string) => void
  loading?: boolean
}

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({
  announcements,
  onEdit,
  onDelete,
  loading
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const getPriorityVariant = (priority: string): "default" | "destructive" | "outline" | "secondary" => {
    switch (priority) {
      case 'urgent': return 'destructive'
      case 'high': return 'default'
      case 'medium': return 'secondary'
      case 'low': return 'outline'
      default: return 'secondary'
    }
  }

  const isActive = (announcement: Announcement) => {
    const now = new Date().toISOString().split('T')[0]
    return announcement.start_date <= now && (!announcement.end_date || announcement.end_date >= now)
  }

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id)
      setDeleteConfirm(null)
    } else {
      setDeleteConfirm(id)
      setTimeout(() => setDeleteConfirm(null), 3000)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-muted rounded w-full mb-2"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (announcements.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No announcements yet</p>
          <p className="text-sm text-muted-foreground mt-2">Create your first announcement to get started</p>
        </CardContent>
      </Card>
    )
  }

  // Sort: pinned first, then by date
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1
    if (!a.is_pinned && b.is_pinned) return 1
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })

  return (
    <div className="space-y-4">
      {sortedAnnouncements.map(announcement => (
        <Card
          key={announcement.id}
          className={announcement.is_pinned ? 'border-primary' : ''}
        >
          <CardContent className="pt-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {announcement.is_pinned && (
                    <Pin className="w-4 h-4 text-primary" />
                  )}
                  <h3 className="text-lg font-semibold">
                    {announcement.title}
                  </h3>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={getPriorityVariant(announcement.priority)} className="capitalize">
                    {announcement.priority}
                  </Badge>

                  {isActive(announcement) ? (
                    <Badge variant="default">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}

                  <span className="text-xs text-muted-foreground">
                    {new Date(announcement.start_date).toLocaleDateString()}
                    {announcement.end_date && ` - ${new Date(announcement.end_date).toLocaleDateString()}`}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(announcement)}
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant={deleteConfirm === announcement.id ? 'destructive' : 'ghost'}
                  size="icon"
                  onClick={() => handleDelete(announcement.id)}
                  title={deleteConfirm === announcement.id ? 'Click again to confirm' : 'Delete'}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <p className="mb-4 whitespace-pre-wrap">
              {announcement.content}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t">
              <div>
                {announcement.author && (
                  <span>
                    By {announcement.author.first_name} {announcement.author.last_name}
                  </span>
                )}
              </div>
              {announcement.target_roles && announcement.target_roles.length > 0 && (
                <div className="flex items-center gap-1">
                  <span>Target:</span>
                  <span className="capitalize">
                    {announcement.target_roles.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default AnnouncementsList
